import { useContext, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { useMutation } from '@tanstack/react-query'
import AppFormSection from 'components/forms/AppFormSection'
import emptyChoicesToNull from 'components/forms/emptyChoicesToNull'
import { popupRefusalOf } from 'components/forms/popupRefusal'
import { refusalOf } from 'components/forms/refusal'
import { NoticesProvider } from 'components/notices/NoticesContext'
import PopupNoticesContainer from 'components/notices/PopupNoticesContainer'
import useValidations from 'hooks/useValidations'
import { applyServerErrors, clearServerErrors } from 'components/forms/serverErrors'
import schema from 'schemas/company'
import { useNotificationContext } from 'units/notifications/NotificationContext'
import FetchContext from '../../context/fetch/FetchContext'
import ModalDialog from '../modal-dialog/ModalDialog'

const INITIAL_VALUES = {
    companyName: '',
    companyId: '',
}

export default function CompanyCreateDialog(props) {
    return (
        <NoticesProvider>
            <CompanyCreateDialogContent {...props} />
        </NoticesProvider>
    )
}

function CompanyCreateDialogContent({
    open = false,
    onClose,
}) {
    const navigate = useNavigate()

    const fetcher = useContext(FetchContext)

    const { addNotification } = useNotificationContext()

    const formik = useRef(null)

    const { showValidations, clearValidations } = useValidations()

    const { fields } = schema.describe()

    const labelOf = (field) => fields[field]?.label

    const mutation = useMutation({
        mutationFn: (values) => {
            const sent = emptyChoicesToNull(values, fields)
            return fetcher.authenticatedFetch({
                method: 'post',
                path: '/companies',
                data: { data: sent },
            })
        },
        onSuccess: (created) => {
            navigate(`/companies/${created.id}`)
        },
        onError: (error) => {
            const { fieldErrors } = popupRefusalOf(error, {
                labelOf,
                fieldNames: Object.keys(INITIAL_VALUES),
            })
            const { notices } = refusalOf(error, { labelOf })

            addNotification({
                type: 'error',
                content: 'יצירת החברה נכשלה',
            })
            showValidations(notices)
            applyServerErrors(formik.current, fieldErrors)
        },
    })

    const submit = () => formik.current?.submitForm()

    const commands = [
        {
            label: 'ביטול',
            testId: 'company-create-cancel',
            color: 'secondary',
            disabled: mutation.isPending,
            onClick: onClose,
        },
        {
            label: 'שמירה',
            testId: 'company-create-save',
            color: 'primary',
            disabled: mutation.isPending,
            onClick: submit,
        },
    ]

    return (
        <ModalDialog
            open={open}
            title="חברה חדשה"
            commands={commands}
            onClose={onClose}
        >
            <PopupNoticesContainer />
            <Formik
                innerRef={formik}
                initialValues={INITIAL_VALUES}
                validationSchema={schema.pick(Object.keys(INITIAL_VALUES))}
                onSubmit={(values) => {
                    clearServerErrors(formik.current)
                    clearValidations()
                    mutation.mutate(values)
                }}
            >
                <Form
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !mutation.isPending) {
                            event.preventDefault()
                            formik.current?.submitForm()
                        }
                    }}
                >
                    <AppFormSection
                        section="basic"
                        fields={fields}
                    />
                </Form>
            </Formik>
        </ModalDialog>
    )
}
